import React from 'react';
import { DynamicForm, Balance, Bolt, ImportExport } from '@mui/icons-material';
import { CustomTabs } from 'components';
import { Alert, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadGroupsListing } from './LoadGroupsListing';
import { LoadBalancingTable } from 'pages/LoadBalancing';
import { Loads } from 'pages/Loads/Loads';
import { RampShedTable } from 'pages/RampShed/RampShedTable';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetAllLoadsQuery, useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useTypedSelector } from '@fiji/common';

type LoadsGroupsProps = { allowedSubTabs?: string[] };

export const LoadsGroups: React.FC<LoadsGroupsProps> = ({ allowedSubTabs }) => {
    const tabsStyle = { display: 'flex', flexDirection: 'row', marginBottom: '1px', width: '100%' };
    const { t } = useTranslation();
    useNewCustomTranslations('RAMP_SHED');
    const { deviceId } = useParams();
    const { currentData: loadsData } = useGetAllLoadsQuery(
        { deviceId, body: { page: 0, size: 10 } },
        { skip: !deviceId, refetchOnMountOrArgChange: true }
    );

    const { data: deviceData }: any = useGetDeviceByIdQuery(deviceId, {
        skip: !deviceId,
    });

    const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);

    React.useEffect(() => {
        const timeout = deviceData?.data?.configurationLockTimeout;
        if (!timeout) return;
        const delay = timeout - Date.now();
        if (delay <= 0) return;
        const timer = setTimeout(forceUpdate, delay);
        return () => clearTimeout(timer);
    }, [deviceData?.data?.configurationLockTimeout]);

    const isRampShedInProgress =
        deviceData?.data?.configurationLockTimeout && deviceData.data.configurationLockTimeout > Date.now();

    const totalLoads = loadsData?.data?.total ?? 0;
    const isRampShedVisible = !deviceId || (loadsData && totalLoads >= 2);
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canViewLoadBalancingTab = hasPermission('automation-management_view-automations');

    const allTabsData: any[] = [
        {
            id: 'loads',
            sx: tabsStyle,
            icon: <Bolt />,
            renderTitle: t('COMMON:LOADS'),
            renderContent: (
                <Box>
                    <Loads isRampShedInProgress={isRampShedInProgress} />
                </Box>
            ),
        },
        {
            id: 'load_groups',
            sx: tabsStyle,
            icon: <DynamicForm />,
            renderTitle: t('COMMON:LOADS_GROUPS'),
            renderContent: (
                <Box>
                    <LoadGroupsListing isRampShedInProgress={Boolean(isRampShedInProgress)} />
                </Box>
            ),
        },
        ...(isRampShedVisible
            ? [
                  {
                      id: 'ramp_shed',
                      sx: tabsStyle,
                      icon: <ImportExport />,
                      renderTitle: t('RAMP_SHED:HEADERS.RAMPSHED'),
                      renderContent: (
                          <Box>
                              <RampShedTable isRampShedInProgress={Boolean(isRampShedInProgress)} />
                          </Box>
                      ),
                  },
              ]
            : []),
        ...(canViewLoadBalancingTab
            ? [
                  {
                      id: 'balancing',
                      sx: tabsStyle,
                      icon: <Balance />,
                      renderTitle: t('COMMON:BALANCING'),
                      renderContent: (
                          <Box>
                              <LoadBalancingTable isRampShedInProgress={Boolean(isRampShedInProgress)} />
                          </Box>
                      ),
                  },
              ]
            : []),
    ];

    const filteredTabsData = allowedSubTabs?.length
        ? allTabsData.filter((tab: any) => allowedSubTabs.includes(tab?.id))
        : allTabsData;
    const tabsData = filteredTabsData.length ? filteredTabsData : allTabsData;
    const initialTab =
        tabsData.find((tab: any) => tab?.id === 'loads')?.id ??
        tabsData.find((tab: any) => tab?.id === 'load_groups')?.id ??
        tabsData?.[0]?.id;

    return (
        <>
            {isRampShedInProgress && (
                <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mx: 3, mt: 3 }}>
                    {t('RAMP_SHED:LABELS.SYNC_IN_PROGRESS')}
                </Alert>
            )}
            <Box sx={{ bgcolor: 'background.paper', m: 3 }}>
                <CustomTabs
                    initialTab={initialTab}
                    tabsData={tabsData}
                    hideAppbar
                    hideToolbar
                    noHashRouting
                    subTab={true}
                />
            </Box>
        </>
    );
};
